
'use client';

import { useDropzone } from 'react-dropzone';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { UploadCloud, Trash2, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useToast } from '@/hooks/use-toast';

interface ImageUploaderProps {
    value: File[];
    onChange: (files: File[]) => void;
    onBlur: () => void;
    maxFiles?: number;
    maxSize?: number;
    currentImageUrls?: string[];
}

export function ImageUploader({ 
    value = [], 
    onChange,
    onBlur,
    maxFiles = 5,
    maxSize = 5 * 1024 * 1024,
    currentImageUrls = []
}: ImageUploaderProps) {
    const [previews, setPreviews] = useState<string[]>([]);
    const [fileErrors, setFileErrors] = useState<string[]>([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const { toast } = useToast();

    const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
        const currentFiles = value;
        const availableSlots = maxFiles - currentFiles.length - currentImageUrls.length;

        if (availableSlots <= 0) {
            setFileErrors([`You can only upload a maximum of ${maxFiles} images.`]);
            return;
        }

        const newFiles = acceptedFiles.slice(0, availableSlots);
        const allFiles = [...currentFiles, ...newFiles];
        
        onChange(allFiles);
        onBlur();

        const newErrors: string[] = [];
        if (acceptedFiles.length > availableSlots) {
             newErrors.push(`You can only add ${availableSlots} more image(s). ${acceptedFiles.length - availableSlots} file(s) were ignored.`);
        }
        rejectedFiles.forEach(file => {
            file.errors.forEach((err: any) => {
                if (err.code === 'file-too-large') {
                    newErrors.push(`Error: ${file.file.name} is larger than ${maxSize / 1024 / 1024}MB`);
                } else if (err.code === 'file-invalid-type') {
                    newErrors.push(`Error: ${file.file.name} has an invalid file type`);
                } else {
                    newErrors.push(`Error: ${file.file.name}: ${err.message}`);
                }
            });
        });
        setFileErrors(newErrors);

    }, [value, onChange, onBlur, maxFiles, maxSize, currentImageUrls]);

    const { getRootProps, getInputProps, isDragActive, isFocused } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/gif': ['.gif'],
            'image/webp': ['.webp'],
        },
        maxSize: maxSize,
        disabled: (value.length + currentImageUrls.length) >= maxFiles,
    });

    useEffect(() => {
        const newPreviews = value.map(file => URL.createObjectURL(file));
        setPreviews(newPreviews);
        return () => newPreviews.forEach(url => URL.revokeObjectURL(url));
    }, [value]);
    
    const removeFile = (index: number) => {
        const newFiles = [...value];
        newFiles.splice(index, 1);
        onChange(newFiles);
    };

    const handleDragStart = (index: number) => {
        if (maxFiles === 1) return;
        setDraggedIndex(index);
    };

    const handleDrop = (index: number) => {
        if (draggedIndex === null || maxFiles === 1) return;
        
        const newFiles = [...value];
        const draggedFile = newFiles.splice(draggedIndex, 1)[0];
        newFiles.splice(index, 0, draggedFile);
        
        onChange(newFiles);
        setDraggedIndex(null);
    };

    const totalImageCount = currentImageUrls.length + value.length;
    const canUploadMore = totalImageCount < maxFiles;
    const showDropzone = canUploadMore || totalImageCount === 0;

    return (
        <div className="flex flex-col gap-4">
             <div
                {...(showDropzone ? getRootProps() : {})}
                className={cn(
                    "flex flex-col items-center justify-center w-full p-8 border-2 border-dashed rounded-lg transition-colors",
                    isDragActive ? "border-primary bg-primary/10" : "border-input",
                    isFocused && "border-primary",
                    !canUploadMore ? "bg-muted/50" : "cursor-pointer hover:border-primary/50",
                    totalImageCount > 0 && "p-4"
                )}
            >
                {showDropzone && <input {...getInputProps()} />}
                
                {totalImageCount > 0 ? (
                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {currentImageUrls.map((url, index) => (
                           <div 
                                key={`current-${index}`}
                                className="relative w-24 h-24 rounded-md overflow-hidden group border-2 border-dashed border-gray-300"
                            >
                                <Image
                                    src={url}
                                    alt={`Current image ${index + 1}`}
                                    fill
                                    sizes="150px"
                                    className="object-cover pointer-events-none"
                                />
                                <div className="absolute top-0 right-0 bg-gray-500 text-white text-xs px-1 py-0.5 rounded-bl-md">Current</div>
                           </div>
                        ))}
                        {previews.map((preview, index) => (
                           <div 
                                key={preview}
                                draggable={value.length > 1}
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDrop(index)}
                                className={cn(
                                    "relative w-24 h-24 rounded-md overflow-hidden group",
                                    value.length > 1 && "cursor-grab",
                                    index === 0 && "border-2 border-primary"
                                )}
                            >
                                <Image
                                    src={preview}
                                    alt={`Preview ${index}`}
                                    fill
                                    sizes="150px"
                                    className="object-cover pointer-events-none"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="icon"
                                        className="h-8 w-8 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile(index);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                                {index === 0 && maxFiles > 1 && (
                                    <div className="absolute bottom-0 w-full bg-primary text-primary-foreground text-xs text-center py-0.5">
                                        Main
                                    </div>
                                )}
                           </div>
                        ))}
                         {canUploadMore && (
                            <div className="w-24 h-24 flex items-center justify-center text-center text-muted-foreground text-sm border-2 border-dashed border-input rounded-md">
                                {totalImageCount > 0 ? 'Add more' : 'Upload'}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center">
                        <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" />
                        <p className="mt-4 text-sm text-foreground">
                            <span className="font-semibold text-primary">Upload file(s)</span> or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            PNG, JPG, GIF, WEBP up to {maxSize / 1024 / 1024}MB. Max {maxFiles} images.
                        </p>
                    </div>
                )}
            </div>
            
            {fileErrors.length > 0 && (
                <div className="mt-2 space-y-2">
                    {fileErrors.map((error, i) => (
                        <div key={i} className="flex items-center text-destructive text-sm p-2 rounded-md bg-destructive/10">
                            <ShieldAlert className="h-4 w-4 mr-2"/>
                            <span>{error}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
